import React, { useMemo } from "react";
import styled from "@emotion/styled";
import LightSvg from "@assets/icons/light.svg";
import { useI18n } from "@core/i18n/i18n";
import { onboardingManager } from "@core/service-container";
import { useObservable } from "micro-observables";
import { OnboardingStepStatus, OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";

interface QuickstartPillProps {
  onClick: () => void;
}

export const QuickstartPill: React.FC<QuickstartPillProps> = ({ onClick }) => {
  const { format } = useI18n();
  const stepsStatus = useObservable(onboardingManager.onboardingStepsStatus);
  const completedCount = useMemo(() => {
    if (!stepsStatus) return 0;

    const isCompleted = (step: OnboardingProgramQuickstartStep) => {
      const status = stepsStatus[step];
      return status === OnboardingStepStatus.DISMISSED || status === OnboardingStepStatus.DISMISSED_BUT_DISPLAYED;
    };

    // Map underlying guide steps to the 6 visible Quickstart items
    const addExercise = isCompleted(OnboardingProgramQuickstartStep.ADD_EXERCISE_GUIDE_01);

    const filterLibrary =
      isCompleted(OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_01) &&
      isCompleted(OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_02);

    const complementary =
      isCompleted(OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01) &&
      isCompleted(OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_02) &&
      isCompleted(OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_03);

    const duplicateSession =
      isCompleted(OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_01) &&
      isCompleted(OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_02);

  const startProgram = isCompleted(OnboardingProgramQuickstartStep.START_GUIDE_02);

    const alternatives = [
      OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_01,
      OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_02,
      OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_03,
    ].every(isCompleted);

    return [
      addExercise,
      filterLibrary,
      complementary,
      alternatives,
      duplicateSession,
      startProgram,
    ].reduce((acc, done) => acc + (done ? 1 : 0), 0);
  }, [stepsStatus]);

  const iconOnly = completedCount === 6;
  return (
    <Pill data-testid="quickstart-pill" onClick={onClick} data-variant={iconOnly ? "icon-only" : "default"}>
      <IconWrapper aria-hidden="true">
        <LightSvg />
      </IconWrapper>
      {!iconOnly && (
        <>
          <LabelText>
            {format("programBuilder.quickstart.pill")}
          </LabelText>
          <CountBadge>
            {format("programBuilder.quickstart.pillCount", { completedCount, totalCount: 6 })}
          </CountBadge>
        </>
      )}
    </Pill>
  );
};

const Pill = styled.button`
  position: relative;
  z-index: 0;
  border: none;
  background: var(--color-primary, #A055F0);
  color: #fff;
  min-width: 188px;
  min-height: 44px;
  line-height: 20px;
  padding: 2px 8px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background .2s ease;
  &:hover { background: var(--color-primary-hover, #8B4AD1); }

  &[data-variant="icon-only"] {
    min-width: 44px;
    width: 44px;
    min-height: 44px;
    padding: 10px;
    justify-content: center;
    gap: 0;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0.9;
  line-height: 0;
  svg { width: 24px; height: 24px; display: block; }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #603390;
  color: #ffffff;
  border-radius: 999px;
  line-height: 20px;
  padding: 2px 8px;
  min-height: 24px;
  min-width: 42px;
  font-weight: 600;
`;

const LabelText = styled.span`
  color: #ffffff;
  font-weight: 700;
  font-size: 16px;
  padding-right: 8px;
`;
